import { environment } from 'src/environments/environment';

/**
 * The portal session: a first-party cookie on the domain shared by Shell and Legacy holding the Okta user id
 * of the signed-in portal user. Both applications keep their own tokens; tokens count only while this marker
 * names their owner. Unlike asking Okta (`/sessions/me`), it is never blocked as a third-party cookie.
 *
 * - written by the Shell when a sign-in completes (the only place a user signs in),
 * - removed by the application in which the user signs out,
 * - read by both applications before trusting their tokens.
 *
 * Name and domains are configured per environment (`portalSession`) and must be identical in the Shell and in
 * the Legacy (`portal-session-marker.ts`) it works with:
 * - the name is environment specific, because a cookie of a parent domain is also sent to the hosts below it
 *   (the prod cookie on `tecalliance.cn` reaches the QA and staging hosts under `tecalliance.cn`),
 * - the domain is configured instead of derived from the host name: Shell and Legacy must write and remove the
 *   very same cookie, and a derived domain may be a public suffix (`azurewebsites.net`) the browser rejects.
 */
const { cookieName, cookieDomains } = environment.portalSession;

/** The configured domain the current host belongs to; host-only (no attribute) otherwise, e.g. on `localhost`. */
function domainAttribute(): string {
  const hostname = window.location.hostname;
  const domain = cookieDomains.find(candidate => hostname === candidate || hostname.endsWith(`.${candidate}`));
  return domain ? `; domain=${domain}` : '';
}

function writeCookie(value: string, attributes: string): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${cookieName}=${encodeURIComponent(value)}; path=/; SameSite=Lax${attributes}${secure}`;
}

export const PortalSessionMarker = {
  /** Okta user id of the signed-in portal user, `null` when nobody is signed in. */
  read(): string | null {
    const cookie = document.cookie.split('; ').find(entry => entry.startsWith(`${cookieName}=`));
    return cookie ? decodeURIComponent(cookie.substring(cookieName.length + 1)) : null;
  },

  /** Session cookie: closing the browser ends the portal session. */
  write(userId: string): void {
    writeCookie(userId, domainAttribute());
  },

  /** Removes the shared cookie and a host-only one that may be left from a host without configured domain. */
  remove(): void {
    writeCookie('', `${domainAttribute()}; Max-Age=0`);
    writeCookie('', '; Max-Age=0');
  }
};
